import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ReactPlayer from "react-player";

interface AudioRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
}

export const AudioRenderer: React.FC<AudioRendererProps> = React.memo(
  ({ url, file, filename, mode = "preview", width = "100%", height = "80px", onLoad, onError, onClick }) => {
    const [audioLoaded, setAudioLoaded] = useState(false);
    const [audioError, setAudioError] = useState<Error | null>(null);
    const [useNativePlayer, setUseNativePlayer] = useState(false);

    // Resolve the audio source (file or URL) - prioritize URL to prevent memory leaks
    const audioSource = useMemo(() => {
      // If URL is provided, use it directly - don't create additional blob URLs
      if (url) {
        return url;
      }
      // Only create blob URL if no URL was provided
      if (file) {
        return URL.createObjectURL(file);
      }
      return undefined;
    }, [url, file]);

    console.log("[AudioRenderer] Rendering with source:", { url, file: file?.name, resolved: audioSource });

    // Cleanup blob URL when component unmounts or file changes
    // Only cleanup if we created the blob URL ourselves (when no url was provided)
    useEffect(() => {
      return () => {
        if (!url && file && audioSource && audioSource.startsWith("blob:")) {
          URL.revokeObjectURL(audioSource);
        }
      };
    }, [url, file, audioSource]);

    // Force onLoad call immediately and after timeout for faster loading state updates
    useEffect(() => {
      // Skip for thumbnail mode
      if (mode === "thumbnail") return;

      // Call onLoad immediately to prevent spinner from showing too long
      if (onLoad && audioSource) {
        console.log("[AudioRenderer] Immediately calling onLoad for audio");
        onLoad();
      }

      // Set a backup timeout in case onReady never fires
      const timer = setTimeout(() => {
        if (!audioLoaded && !audioError && onLoad) {
          console.log("[AudioRenderer] Forcing onLoad after timeout for audio");
          onLoad();
        }
      }, 500);

      return () => clearTimeout(timer);
    }, [audioSource, audioLoaded, audioError, onLoad, mode]);

    // After 2 seconds with error, try native audio player as fallback
    useEffect(() => {
      // Skip for thumbnail mode
      if (mode === "thumbnail") return;

      if (audioError && !useNativePlayer) {
        const timer = setTimeout(() => {
          console.log("[AudioRenderer] Switching to native audio player as fallback");
          setUseNativePlayer(true);
          // Reset error since we're trying a fallback
          setAudioError(null);
          // Notify parent that we're loading
          if (onLoad) onLoad();
        }, 2000);

        return () => clearTimeout(timer);
      }
    }, [audioError, useNativePlayer, onLoad, mode]);

    // Early return if no audio source provided
    if (!audioSource) {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1
          }}
        >
          <Typography color='text.secondary'>No audio source provided</Typography>
        </Box>
      );
    }

    // Thumbnail mode
    if (mode === "thumbnail") {
      return (
        <Box
          sx={{
            width,
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: onClick ? "pointer" : "default",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: 1
          }}
          onClick={onClick}
        >
          <Typography>🎵 Audio</Typography>
        </Box>
      );
    }

    const handlePlayerReady = () => {
      console.log("[AudioRenderer] ReactPlayer onReady fired for audio");
      setAudioLoaded(true);
      if (onLoad) {
        console.log("[AudioRenderer] Calling parent onLoad callback");
        onLoad();
      }
    };

    const handlePlayerError = (error: Error) => {
      console.error("[AudioRenderer] Audio player error:", error);
      setAudioError(error);
      if (onError) {
        console.log("[AudioRenderer] Calling parent onError callback");
        onError(error);
      }
    };

    // If we're using native player as a fallback
    if (useNativePlayer) {
      return (
        <Box
          sx={{
            p: 3,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f0f8ff", // Light blue to distinguish from ReactPlayer
            border: "2px solid #4CAF50", // Green border for native player
            borderRadius: 1
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Using native audio player
          </Typography>
          <audio
            controls
            src={audioSource}
            style={{ width: "100%", maxWidth: "400px" }}
            onLoadedData={() => {
              console.log("[AudioRenderer] Native audio loaded");
              onLoad && onLoad();
            }}
            onError={() => {
              console.error("[AudioRenderer] Native audio error");
              onError && onError();
            }}
          >
            Your browser does not support the audio element.
          </audio>
          {audioError && (
            <Typography color='error' variant='caption' sx={{ mt: 1 }}>
              Error with ReactPlayer: {audioError.message}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 3,
          minWidth: 300,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5", // Light gray for ReactPlayer
          border: "2px solid #2196F3", // Blue border for ReactPlayer
          borderRadius: 1
        }}
      >
        <ReactPlayer
          url={audioSource}
          controls={true}
          width='100%'
          height='60px'
          config={{
            file: {
              forceAudio: true,
              attributes: {
                controlsList: "nodownload",
                preload: "auto"
              }
            }
          }}
          onReady={handlePlayerReady}
          onError={handlePlayerError}
          onStart={handlePlayerReady} // Also treat start as ready
          onPlay={handlePlayerReady} // Also treat play as ready
        />
        {audioError && (
          <Typography color='error' variant='caption' sx={{ mt: 1 }}>
            Error loading audio: {audioError.message}
          </Typography>
        )}
      </Box>
    );
  }
);

export default AudioRenderer;
